import React, { useState, useEffect } from 'react';
import { settingsAPI } from '../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import LocationPicker from '../components/LocationPicker';
import {
  Settings,
  Mail,
  Shield,
  Bell,
  Save,
  AlertTriangle,
  Plus,
  X,
  CheckCircle2,
  MapPin,
} from 'lucide-react';

const SystemSettings = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newAlertEmail, setNewAlertEmail] = useState('');

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (isAdmin) {
      loadSettings();
    }
  }, [isAdmin]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const res = await settingsAPI.get();
      setSettings(res.data);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const updateData = {
        safety_officer_email: settings.safety_officer_email || null,
        safety_officer_name: settings.safety_officer_name || null,
        clock_alert_emails: settings.clock_alert_emails || [],
        email_notifications_enabled: settings.email_notifications_enabled,
        master_geofence: settings.master_geofence || null,
      };
      await settingsAPI.update(updateData);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const addAlertEmail = () => {
    if (!newAlertEmail || !newAlertEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (settings.clock_alert_emails?.includes(newAlertEmail)) {
      toast.error('Email already in list');
      return;
    }
    setSettings({
      ...settings,
      clock_alert_emails: [...(settings.clock_alert_emails || []), newAlertEmail],
    });
    setNewAlertEmail('');
  };

  const removeAlertEmail = (email) => {
    setSettings({
      ...settings,
      clock_alert_emails: settings.clock_alert_emails.filter((e) => e !== email),
    });
  };

  if (!isAdmin) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-2" />
          <h2 className="text-lg font-semibold text-red-700">Access Denied</h2>
          <p className="text-red-600">Only administrators can access system settings.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-1/4"></div>
          <div className="h-64 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <Settings className="w-6 h-6 text-blue-600" />
          System Settings
        </h1>
        <p className="text-slate-600">Configure system-wide settings and notifications</p>
      </div>

      {/* Email Notifications Toggle */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${settings?.email_notifications_enabled ? 'bg-green-100' : 'bg-slate-100'}`}>
              <Bell className={`w-5 h-5 ${settings?.email_notifications_enabled ? 'text-green-600' : 'text-slate-400'}`} />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Email Notifications</h3>
              <p className="text-sm text-slate-500">Enable or disable all email notifications</p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings?.email_notifications_enabled || false}
              onChange={(e) => setSettings({ ...settings, email_notifications_enabled: e.target.checked })}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
      </div>

      {/* Safety Officer Settings */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
            <Shield className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">Safety Officer</h3>
            <p className="text-sm text-slate-500">Configure the designated safety officer for incident notifications</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Safety Officer Name</label>
            <input
              type="text"
              value={settings?.safety_officer_name || ''}
              onChange={(e) => setSettings({ ...settings, safety_officer_name: e.target.value })}
              placeholder="e.g., John Smith"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Safety Officer Email</label>
            <input
              type="email"
              value={settings?.safety_officer_email || ''}
              onChange={(e) => setSettings({ ...settings, safety_officer_email: e.target.value })}
              placeholder="safety@company.com"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            />
          </div>
        </div>

        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-700">
            <strong>Note:</strong> The safety officer will receive email notifications whenever a new incident is reported, regardless of severity level.
          </div>
        </div>
      </div>

      {/* Clock Alert Recipients */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <Mail className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">Clock Alert Recipients</h3>
            <p className="text-sm text-slate-500">Additional email addresses to receive clock-in/out alerts (managers always receive alerts)</p>
          </div>
        </div>

        {/* Current emails list */}
        <div className="space-y-2 mb-4">
          {settings?.clock_alert_emails?.length > 0 ? (
            settings.clock_alert_emails.map((email, index) => (
              <div key={index} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                <span className="text-sm text-slate-700">{email}</span>
                <button
                  onClick={() => removeAlertEmail(email)}
                  className="p-1 hover:bg-slate-200 rounded"
                >
                  <X className="w-4 h-4 text-slate-500" />
                </button>
              </div>
            ))
          ) : (
            <p className="text-sm text-slate-500 italic">No additional recipients configured. Managers will still receive alerts.</p>
          )}
        </div>

        {/* Add new email */}
        <div className="flex gap-2">
          <input
            type="email"
            value={newAlertEmail}
            onChange={(e) => setNewAlertEmail(e.target.value)}
            placeholder="Add email address..."
            className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm"
            onKeyDown={(e) => e.key === 'Enter' && addAlertEmail()}
          />
          <button
            onClick={addAlertEmail}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 text-sm"
          >
            <Plus className="w-4 h-4" />
            Add
          </button>
        </div>

        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
          <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-700">
            <strong>Automatic alerts:</strong> The system automatically checks for clock-in/out exceptions at 10:15 AM (not clocked in) and 6:15 PM (not clocked out) South Africa time.
          </div>
        </div>
      </div>

      {/* Master Geofence Settings */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
            <MapPin className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">Master Geofence (Company Office)</h3>
            <p className="text-sm text-slate-500">Default geofence area that applies to all restricted users</p>
          </div>
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-700 mb-1">Location Name</label>
          <input
            type="text"
            value={settings?.master_geofence?.name || ''}
            onChange={(e) => setSettings({
              ...settings,
              master_geofence: { ...settings?.master_geofence, name: e.target.value }
            })}
            placeholder="e.g., Company Office, Main Building"
            className="w-full md:w-1/2 px-3 py-2 border border-slate-200 rounded-lg text-sm"
          />
        </div>

        <LocationPicker
          latitude={settings?.master_geofence?.latitude || null}
          longitude={settings?.master_geofence?.longitude || null}
          radius={settings?.master_geofence?.radius || 20}
          onLocationChange={(lat, lng) => setSettings({
            ...settings,
            master_geofence: { 
              ...settings?.master_geofence, 
              latitude: lat,
              longitude: lng 
            }
          })}
          onRadiusChange={(r) => setSettings({
            ...settings,
            master_geofence: { ...settings?.master_geofence, radius: r }
          })}
        />

        {settings?.master_geofence?.latitude && settings?.master_geofence?.longitude && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle2 className="w-5 h-5" />
              <span className="font-medium">Master Geofence Configured</span>
            </div>
            <p className="text-sm text-green-600 mt-1">
              Location: {settings.master_geofence.latitude.toFixed(6)}, {settings.master_geofence.longitude.toFixed(6)} 
              • Radius: {settings.master_geofence.radius || 20}m
              {settings.master_geofence.name && ` • ${settings.master_geofence.name}`}
            </p>
          </div>
        )}

        {!settings?.master_geofence?.latitude && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-700">
              <strong>Not Configured:</strong> Search for an address or click on the map to set the master geofence location.
              Users with geofence restrictions will not be able to login until this is configured.
            </div>
          </div>
        )}
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg flex items-center gap-2"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Save Settings
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default SystemSettings;
